<template>
    <template v-for="(b, index) in goalBlocks"
              :key="`recovery-goal-blocks-${index}`">
        <recovery-goal-block v-model="goalBlocks[index]"
                             :recovery-goal-block-number="index + 1"
                             :patient-id="patientId"
                             :edit-state="editState"
                             :review-statuses="reviewStatuses"
                             :can-be-reviewed="canBeReviewed"
                             :url-head="urlHead"
                             :edit-mode-on-create="false"
                             :goal-plan-id="goalPlanId"
                             @cancel-pressed="cancelPressed"
                             @update-pressed="updatePressed"
                             @delete-pressed="deletePressed" />
        <hr>
    </template>
    <div v-if="editable"
         class="box-footer">
        <button type="button"
                class="btn portal-btn outline icon-button"
                @click="addClicked">
            <i class="icon iconbutton icon-plus" />
        </button>
    </div>
    <div v-if="editModeOn">
        <recovery-goal-block v-model="newGoalBlock"
                             :recovery-goal-block-number="index + 1"
                             :patient-id="patientId"
                             :edit-state="editState"
                             :review-statuses="reviewStatuses"
                             :url-head="urlHead"
                             :edit-mode-on-create="true"
                             :goal-plan-id="goalPlanId"
                             @cancel-pressed="cancelPressed"
                             @update-pressed="updatePressed"
                             @delete-pressed="deletePressed" />
    </div>
</template>

<script setup>
    import { defineProps, defineEmits, computed, ref, nextTick } from 'vue';
    import axios from 'axios';
    import { axiosConfig, axiosError } from '../../js/AjaxUtilities.js'
    import { canEdit } from '../../js/CalcGoalPlanEditState.js';
    import recoveryGoalBlock from "./RecoveryGoalBlock.vue";

    const props = defineProps({
        modelValue: {
            type: Array,
            default: null
        },
        patientId: {
            type: Number,
            default: 0
        },
        goalTypeId: {
            type: Number,
            default: 0
        },
        editState: {
            type: Number,
            default: 0
        },
        reviewStatuses: {
            type: Array,
            default: null
        },
        canBeReviewed: {
            type: Boolean,
            default: false
        },
        urlHead: {
            type: String,
            default: null
        },
        goalPlanId: {
            type: Number,
            default: 0
        }
    });

    const emit = defineEmits(['update:modelValue', 'editFinished']);

    const sending = ref(false);
    const newGoalBlock = ref(null);
    const editModeOn = ref(false);

    const goalBlocks = computed({
        get: () => props.modelValue,
        set: newValue => emit("update:modelValue", newValue)
    });

    const editable = computed(() => canEdit(props.editState, 2));

    function deletePressed(recoveryBlockId) {
        let index = goalBlocks.value.findIndex((el) => el.goalPlanRecoveryGoalBlockId == recoveryBlockId);
        if (index >= 0) {
            sending.value = true;
            let data = {
                id: recoveryBlockId,
                patientId: props.patientId
            };

            axios
                .post(`/${props.urlHead}/DeleteRecoveryGoalBlock`, data, axiosConfig())
                .then(function (response) {
                    if (response.data.success) {
                        sendReducedBlocks(index);
                    } else {
                        alert(`Failed to update goal items: ${response.data.message}`);
                    }
                    sending.value = false;
                })
                .catch(function (error) {
                    axiosError("Delete recovery goal block", axios, error);
                })
                .finally(function () {
                    sending.value = true;
                });
        }
    }

    function sendReducedBlocks(index) {
        let blocks = [...goalBlocks.value];
        blocks.splice(index, 1);
        emit('update:modelValue', blocks);
    }

    function addClicked() {
        let data = {
            id : props.goalTypeId,
            patientId : props.patientId
        };
        axios
            .post(`/${props.urlHead}/GetNewRecoveryGoalBlock`, data, axiosConfig())
            .then(function (response) {
                if (response.data.success) {
                    newGoalBlock.value = response.data.newBlock;
                    editModeOn.value = true;
                } else {
                    alert(`Failed to update goal items: ${response.data.message}`);
                }
                sending.value = false;
            })
            .catch(function (error) {
                axiosError("Get new recovery goal block", axios, error);
            })
            .finally(function () {
                sending.value = true;
            });
    }

    function sendAddedBlocks(newBlock) {
        let blocks = [...goalBlocks.value];
        blocks.push(newBlock);
        emit('update:modelValue', blocks);
    }

    function updateBlocks(updatedBlock) {
        let blocks = [...goalBlocks.value];
        blocks = blocks.map(block => block.goalPlanRecoveryGoalBlockId === updatedBlock.goalPlanRecoveryGoalBlockId ? updatedBlock : block);
        emit('update:modelValue', blocks);
    }

    function cancelPressed() {
        editModeOn.value = false;
    }

    function updatePressed(updateGoalItems) {
        nextTick(() => updatePressedImpl(updateGoalItems));
    }

    function updatePressedImpl(updateGoalItems) {
        if (updateGoalItems.goalBlockId == 0) {
            editModeOn.value = false;
            updateGoalItems.goalTypeId = props.goalTypeId;
            AddRecoveryBlock(updateGoalItems);
        } else {
            UpdateRecoveryBlock(updateGoalItems);
        }
    }

    function AddRecoveryBlock(updateGoalItems) {
        axios
            .post(`/${props.urlHead}/AddRecoveryGoalBlock`, updateGoalItems, axiosConfig())
            .then(function (response) {
                if (response.data.success) {
                    sendAddedBlocks(response.data.goalBlock);
                } else {
                    alert(`Failed to update goal items: ${response.data.message}`);
                }
            })
            .catch(function (error) {
                axiosError("Add recovery goal block", axios, error);
            })
            .finally(function () {
                emit("editFinished");
            });
    }

    function UpdateRecoveryBlock(updateGoalItems) {
        axios
            .post(`/${props.urlHead}/UpdateGoalItems`, updateGoalItems, axiosConfig())
            .then(function (response) {
                if (response.data.success) {
                    updateBlocks(response.data.goalBlock);
                } else {
                    alert(`Failed to update goal items: ${response.data.message}`);
                }
                sending.value = false;
            })
            .catch(function (error) {
                axiosError("Update goals", axios, error);
            })
            .finally(function () {
                emit("editFinished");
            });
    }

</script>